import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, RotateCcw } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import Gauge from "../components/Gauge";
import { TOOLS, CARS_QUESTIONS } from "../data/mock";

export const Tools = () => (
  <PageShell>
    <section className="hero-grid border-b border-neutral-200">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-14">
        <Eyebrow>Free interactive tools</Eyebrow>
        <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight text-ink leading-[1.02]">Run a scorer.<br />Get a roadmap.</h1>
        <p className="mt-6 text-[16px] text-ink/60 max-w-2xl leading-relaxed">Every Australian BFSI compliance team uses these — gap assessments, maturity scorers and penalty calculators. Free, no login.</p>
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLS.map((t) => (
          <Link key={t.slug} to={t.interactive ? `/tools/${t.slug}` : "/contact"} data-testid={`tool-card-${t.slug}`}
            className={`group rounded-lg p-7 transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-1 ${t.interactive ? "bg-ink text-white hover:shadow-[0_20px_50px_rgba(15,27,45,0.25)]" : "bg-white border border-neutral-200 hover:border-accent/40 hover:shadow-[0_16px_40px_rgba(15,27,45,0.08)]"}`}>
            <div className="flex items-center justify-between">
              <span className={`font-mono text-[10px] tracking-[0.2em] uppercase ${t.interactive ? "text-accent-light" : "text-accent"}`}>{t.tag}</span>
              <ArrowUpRight size={16} className={t.interactive ? "text-white/40 group-hover:text-accent-light" : "text-neutral-300 group-hover:text-accent"} />
            </div>
            <h2 className={`font-heading text-xl font-bold mt-3 ${t.interactive ? "text-white" : "text-ink"}`}>{t.name}</h2>
            <p className={`text-[13.5px] leading-relaxed mt-3 ${t.interactive ? "text-white/60" : "text-ink/55"}`}>{t.desc}</p>
            <span className={`inline-block font-mono text-[11px] mt-5 ${t.interactive ? "text-accent-light" : "text-neutral-400"}`}>{t.interactive ? "INTERACTIVE — RUN NOW" : "COMING SOON — REGISTER INTEREST"}</span>
          </Link>
        ))}
      </div>
    </section>
  </PageShell>
);

const OPTIONS = [
  { label: "Not in place", pct: 0 },
  { label: "Partial", pct: 0.5 },
  { label: "Fully in place", pct: 1 },
];

export const CarsCalculator = () => {
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const answeredAll = CARS_QUESTIONS.every((q) => answers[q.id] !== undefined);
  const score = Math.round(CARS_QUESTIONS.reduce((sum, q) => sum + (answers[q.id] ?? 0) * q.weight, 0));
  const band = score >= 85 ? "Strong" : score >= 65 ? "Adequate" : score >= 45 ? "Developing" : "Exposed";
  const advice = {
    Strong: "Posture is well ahead of peer median. Focus on sustaining evidence quality for the next tripartite review cycle.",
    Adequate: "Core obligations covered. Prioritise the partial areas — APRA's supervisory focus is on evidence of operation, not policy existence.",
    Developing: "Material gaps against CPS 234 / CPS 230 expectations. Sequence remediation by regulatory deadline exposure and board accountability risk.",
    Exposed: "Immediate attention required. Start with information security capability and critical-operations mapping — these carry direct board accountability.",
  }[band];

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8 pt-14 pb-10">
          <Link to="/tools" className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-neutral-400 hover:text-accent transition-colors duration-150 mb-8" data-testid="back-to-tools">
            <ArrowLeft size={13} /> ALL TOOLS
          </Link>
          <Eyebrow>Flagship metric · CARS v1.0</Eyebrow>
          <h1 className="font-heading text-4xl lg:text-[52px] font-bold tracking-tight text-ink leading-[1.05]">Composite Australia Risk Score</h1>
          <p className="mt-4 text-[15px] text-ink/60 max-w-2xl leading-relaxed">Six weighted questions across CPS 234, CPS 230, Essential Eight, privacy, third-party and resilience posture. Answer honestly — the score is computed locally in your browser.</p>
        </div>
      </section>
      <section className="py-14">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          {!submitted ? (
            <div className="space-y-5">
              {CARS_QUESTIONS.map((q, i) => (
                <div key={q.id} className="border border-neutral-200 rounded-lg p-6 bg-white" data-testid={`cars-question-${q.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-[15px] text-ink font-medium leading-relaxed">
                      <span className="font-mono text-[11px] text-accent mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {q.text}
                    </p>
                    <span className="font-mono text-[10.5px] text-neutral-400 shrink-0">w · {q.weight}</span>
                  </div>
                  <div className="flex flex-wrap gap-2.5 mt-4">
                    {OPTIONS.map((o) => (
                      <button key={o.label} onClick={() => setAnswers({ ...answers, [q.id]: o.pct })} data-testid={`cars-${q.id}-${o.pct}`}
                        className={`text-[13px] font-medium px-4 py-2 rounded-md border transition-colors duration-150 ${answers[q.id] === o.pct ? "bg-ink text-white border-ink" : "border-neutral-300 text-ink/60 hover:border-ink"}`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              <button onClick={() => setSubmitted(true)} disabled={!answeredAll} data-testid="cars-submit-btn"
                className="w-full bg-accent text-white rounded-md py-4 text-[15px] font-semibold hover:bg-accent-dark transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
                {answeredAll ? "Compute my CARS score" : `Answer all ${CARS_QUESTIONS.length} questions to compute`}
              </button>
            </div>
          ) : (
            <div className="border border-neutral-200 rounded-lg p-8 lg:p-12 bg-white text-center" data-testid="cars-result">
              <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-accent mb-6">Your composite score</p>
              <div className="flex justify-center"><Gauge value={score} size={300} /></div>
              <h2 className="font-heading text-3xl font-bold text-ink mt-4">{band}</h2>
              <p className="text-[15px] text-ink/65 leading-[1.8] max-w-xl mx-auto mt-4">{advice}</p>
              <div className="flex flex-wrap justify-center gap-3 mt-8">
                <button onClick={() => { setSubmitted(false); setAnswers({}); }} data-testid="cars-reset-btn"
                  className="inline-flex items-center gap-2 border border-neutral-300 text-ink px-5 py-2.5 rounded-md text-[13.5px] font-semibold hover:border-ink transition-colors duration-200">
                  <RotateCcw size={14} /> Run again
                </button>
                <Link to="/contact" className="bg-ink text-white px-5 py-2.5 rounded-md text-[13.5px] font-semibold hover:bg-ink/85 transition-colors duration-200">Discuss my result</Link>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageShell>
  );
};
